<template>
  <div class="tw-space-y-2">
    <Text v-if="!order.length">{{ t('networking.wan_no_interfaces') }}</Text>

    <template v-else>
      <div v-for="(type, index) in order" :key="type" class="tw-flex tw-items-center tw-gap-3">
        <!-- The priority belongs to the position, not to the interface: rows move, numbers stay. -->
        <Text class="tw-w-6 tw-shrink-0 tw-text-center tw-font-black" :aria-label="t('networking.wan_col_priority')">
          {{ index + 1 }}
        </Text>

        <Box
          rounded="large"
          :level="2"
          :class="[
            isDropTarget(index) ? 'tw-border-purple/25' : 'tw-border-transparent',
            { 'tw-cursor-grab': !disabled },
          ]"
          class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3 tw-border tw-px-3 tw-py-2"
          :draggable="disabled ? 'false' : 'true'"
          @dragstart="onDragStart($event, index)"
          @dragover.prevent="onDragOver(index)"
          @drop.prevent="onDrop(index)"
          @dragend="onDragEnd"
        >
          <Icon :name="MenuSvg" class="tw-size-4 tw-shrink-0 tw-fill-white/50" aria-hidden="true" />

          <Icon
            :name="ICONS[type]"
            class="tw-size-6 tw-shrink-0"
            :class="STATUS_ICON_CLASSES[status(type)]"
            role="img"
            v-tooltip="t(`networking.iface_status_${status(type)}`)"
            :aria-label="t(`networking.iface_status_${status(type)}`)"
          />

          <div class="tw-min-w-0 tw-flex-1">
            <Text class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2">
              {{ t(`networking.iface_${type}`) }}

              <Text v-if="type === 'wifi' && byType[type]?.provider" tag="span" class="tw-font-mono">
                [{{ byType[type].provider }}]
              </Text>

              <Text v-if="byType[type]?.active" tag="span" class="tw-font-black tw-text-state-succ">
                {{ t('networking.wan_active') }}
              </Text>
            </Text>

            <Text v-if="byType[type]?.ip" size="small" class="tw-opacity-80">
              {{ t('networking.wan_ip') }} <span class="tw-font-mono">{{ byType[type].ip }}</span>
            </Text>

            <Text v-if="byType[type]?.mac" size="small" class="tw-opacity-80">
              {{ t('networking.wan_mac') }} <span class="tw-font-mono">{{ byType[type].mac }}</span>
            </Text>
          </div>

          <CircularButton
            v-if="SETTINGS_TYPES.includes(type)"
            icon="settings"
            size="small"
            color="transparentWhite"
            :disabled="disabled"
            :aria-label="t(`networking.${type}_settings`)"
            v-tooltip="t(`networking.${type}_settings`)"
            @click="emit('settings', type)"
          />

          <span v-tooltip="{ content: lockedReason(type), disabled: !lockedReason(type) }" class="tw-shrink-0">
            <Checkbox
              type="switch"
              :model-value="Boolean(byType[type]?.enabled)"
              :disabled="disabled || Boolean(lockedReason(type))"
              :confirm="(checked) => requestToggle(type, checked)"
            />
          </span>
        </Box>
      </div>

      <Text size="small" class="tw-opacity-80">{{ t('networking.iface_status_legend') }}</Text>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Checkbox, CircularButton, Icon, Text } from '@slideslive/fuse-kit/vue';

import MenuSvg from '@/assets/mdi/menu.svg';
import SignalCellular3Svg from '@/assets/mdi/signal-cellular-3.svg';
import SitemapOutlineSvg from '@/assets/mdi/sitemap-outline.svg';
import WifiSvg from '@/assets/mdi/wifi.svg';

const props = defineProps({
  // Interfaces from /state → data.wan.failover.interfaces (enabled, status, SSID, IP, active).
  interfaces: { type: Array, default: () => [] },
  // Types in the order to show, highest priority first. While a reorder waits for its
  // confirmation, this is the new order.
  order: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});

// reorder(newOrder) after a drop, toggle(type, enabled) from a switch, settings(type) from the gear.
// The parent confirms and applies them; the list shows only what /state reports.
const emit = defineEmits(['reorder', 'toggle', 'settings']);

const { t } = useI18n();

const ICONS = {
  ethernet: SitemapOutlineSvg,
  wifi: WifiSvg,
  cellular: SignalCellular3Svg,
};

// Interfaces with something to set up. Ethernet and cellular have nothing yet.
const SETTINGS_TYPES = ['wifi'];

const STATUS_ICON_CLASSES = {
  online: 'tw-fill-state-succ',
  offline: 'tw-fill-orange',
  disabled: 'tw-fill-white/50',
};

const byType = computed(() => Object.fromEntries(props.interfaces.map((iface) => [iface.type, iface])));

const status = (type) => {
  const iface = byType.value[type];
  if (!iface?.enabled) return 'disabled';
  return iface.online ? 'online' : 'offline';
};

const enabledCount = computed(() => props.interfaces.filter((iface) => iface.enabled).length);

// Why the switch can not be turned off, or '' when it can.
const lockedReason = (type) => {
  if (!byType.value[type]?.enabled) return '';
  if (type === 'ethernet') return t('networking.wan_ethernet_locked');
  if (enabledCount.value <= 1) return t('networking.wan_last_enabled');
  return '';
};

// The click must not flip the switch: it only asks the parent, and the switch follows /state.
// Returning false makes the checkbox cancel the click.
const requestToggle = (type, enabled) => {
  emit('toggle', type, Boolean(enabled));
  return false;
};

const dragIndex = ref(null);
const dropIndex = ref(null);

const isDropTarget = (index) => dragIndex.value !== null && dropIndex.value === index && dragIndex.value !== index;

const onDragStart = (event, index) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }

  dragIndex.value = index;

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox does not start a drag without data.
    event.dataTransfer.setData('text/plain', props.order[index]);
  }
};

const onDragOver = (index) => {
  if (dragIndex.value === null) return;
  dropIndex.value = index;
};

const onDrop = (index) => {
  const from = dragIndex.value;
  onDragEnd();
  if (from === null || from === index || props.disabled) return;

  const next = [...props.order];
  const [item] = next.splice(from, 1);
  next.splice(index, 0, item);
  emit('reorder', next);
};

const onDragEnd = () => {
  dragIndex.value = null;
  dropIndex.value = null;
};
</script>
